import {useEffect, useRef, useState} from 'react'
import {ArrowUpRightIcon} from '@primer/octicons-react'

import type {Artifact} from '../../utils/artifacts/artifact-core'
import {ARTIFACTS_COLLAPSE_LABEL, ARTIFACTS_EXPAND_LABEL, ARTIFACTS_LABEL} from './artifacts-labels'
import {useOpenArtifactPreview} from './use-open-artifact-preview'
import {ActionBarTrigger} from '../task-view/AhpTaskChatInputActionBar/ActionBarTrigger'
import {AhpTaskChatInputActionBar} from '../task-view/AhpTaskChatInputActionBar/AhpTaskChatInputActionBar'
import {ArtifactItem} from '../task-view/AhpTaskChatInputActionBar/ArtifactItem'
import {ItemsPanel} from '../task-view/AhpTaskChatInputActionBar/ItemsPanel'
import {useSessionArtifacts} from '../../hooks/use-session-artifacts'
import styles from './SessionArtifacts.module.css'

interface ArtifactsProps {
  artifacts: Artifact[]
}

/**
 * The artifacts control for an agent session, wired to the surrounding session
 * context. The transcript-specific data shaping stays in useSessionArtifacts;
 * the rendered controls reuse the AHP action bar primitives.
 */
export function SessionArtifacts() {
  const artifacts = useSessionArtifacts()
  return <Artifacts artifacts={artifacts} />
}

export function Artifacts({artifacts}: ArtifactsProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedPath, setSelectedPath] = useState<string | undefined>(undefined)
  const openInWorkbench = useOpenArtifactPreview()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const wasExpandedRef = useRef(false)

  // Only whole-file writes carry their contents, so only those can be handed to
  // the workbench; the rest keep the inline fallback.
  const canOpenInWorkbench = (artifact: Artifact) => openInWorkbench !== undefined && artifact.content !== undefined

  const selected = artifacts.find(artifact => artifact.absolutePath === selectedPath)

  // The design shows bare file names, which is ambiguous when the agent wrote
  // two files of the same name in different folders. Only those rows fall back
  // to showing their directory, so the common case stays uncluttered.
  const names = artifacts.map(artifact => artifact.fileName)
  const ambiguousNames = new Set(names.filter((name, index) => names.indexOf(name) !== index))

  useEffect(() => {
    if (isExpanded) {
      closeButtonRef.current?.focus()
      wasExpandedRef.current = true
    } else if (wasExpandedRef.current) {
      triggerRef.current?.focus()
      wasExpandedRef.current = false
    }
  }, [isExpanded])

  // Mirrors the changed-files header, which hides itself when there is nothing
  // to show rather than rendering an empty affordance above the composer.
  if (artifacts.length === 0) return null

  return (
    <div className={styles.root} data-testid="session-artifacts">
      {isExpanded && (
        <div data-testid="session-artifacts-panel">
          <ItemsPanel
            title={ARTIFACTS_LABEL}
            closeButtonRef={closeButtonRef}
            onClose={() => setIsExpanded(false)}
            items={artifacts.map(artifact => (
              <ArtifactItem
                key={artifact.absolutePath}
                label={artifact.fileName}
                description={
                  ambiguousNames.has(artifact.fileName) && artifact.directory ? artifact.directory : undefined
                }
                trailingVisual={canOpenInWorkbench(artifact) ? ArrowUpRightIcon : undefined}
                active={artifact.absolutePath === selectedPath}
                onSelect={event => {
                  if (canOpenInWorkbench(artifact)) {
                    openInWorkbench?.(artifact, event as React.MouseEvent)
                    setSelectedPath(artifact.absolutePath)
                    return
                  }
                  setSelectedPath(current => (current === artifact.absolutePath ? undefined : artifact.absolutePath))
                }}
              />
            ))}
          />
        </div>
      )}
      {isExpanded && selected && !canOpenInWorkbench(selected) && (
        <div className={styles.preview} data-testid="session-artifacts-preview">
          <div className={styles.previewHeader}>{selected.displayPath}</div>
          {selected.preview ? (
            <pre className={styles.previewBody}>{selected.preview}</pre>
          ) : (
            <p className={styles.previewEmpty}>No preview available for this file.</p>
          )}
        </div>
      )}
      <AhpTaskChatInputActionBar>
        <ActionBarTrigger
          ref={triggerRef}
          count={artifacts.length}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? ARTIFACTS_COLLAPSE_LABEL : ARTIFACTS_EXPAND_LABEL}
        >
          {ARTIFACTS_LABEL}
        </ActionBarTrigger>
      </AhpTaskChatInputActionBar>
    </div>
  )
}
